import { useViewer } from "~/auth/use-viewer"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { GAME_INTENSITIES, GAME_GENRES } from "~/types/game-preferences"
import { DistanceUnit, GameGenre, GameIntensity } from "~/__generated__/graphql"
import { gql } from "~/__generated__"
import { Section } from "~/ui/section"
import { PlacesAutocomplete } from "~/ui/places-autocomplete"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { useValidationErrors } from "~/common/use-validation-errors"
import { TextField } from "~/fields/text-field"
import { TablePageLayout } from "~/layouts/table-page-layout"
import { Button } from "~/ui/button"
import { Form } from "~/ui/form"
import { useToast } from "~/ui/use-toast"
import { Link } from "react-router-dom"
import { useNavigate } from "react-router-dom"
import { rootPath, profilePath } from "~/common/paths"
import { AvatarUpload } from "./avatar-upload"
import { MapPinHouseIcon, UserCircle, Bell, Dices } from "lucide-react"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "~/ui/select"
import { Checkbox } from "~/ui/checkbox"
import { getMetaContent } from "~/common/get-meta-content"
import { getAnalytics } from "firebase/analytics"
import { FirebaseApp, initializeApp } from "firebase/app"
import { Messaging, getMessaging, getToken, onMessage } from "firebase/messaging"
import invariant from "tiny-invariant"

const environment = getMetaContent("environment")
// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const workerUrl = "/firebase-messaging-sw.js"
const firebaseConfig =
  environment === "production"
    ? {
        apiKey: "AIzaSyC9Ly0CkENwZe21EozklSqlVBscI3-RWko",
        authDomain: "letsdo-18895.firebaseapp.com",
        projectId: "letsdo-18895",
        storageBucket: "letsdo-18895.firebasestorage.app",
        messagingSenderId: "998761585671",
        appId: "1:998761585671:web:694202bad692f849159ea2",
        measurementId: "G-WRK1LYT6GN",
      }
    : {
        apiKey: "AIzaSyC9Ly0CkENwZe21EozklSqlVBscI3-RWko",
        authDomain: "letsdo-18895.firebaseapp.com",
        projectId: "letsdo-18895",
        storageBucket: "letsdo-18895.firebasestorage.app",
        messagingSenderId: "998761585671",
        appId: "1:998761585671:web:5b770f060adac4f5159ea2",
        measurementId: "G-2PKL3977ZR",
      }

// Initialize Firebase
const app = initializeApp(firebaseConfig)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const analytics = getAnalytics(app)

const mutation = gql(/* GraphQL */ `
  mutation UpdateProfile($input: UserUpdateInput!) {
    userUpdate(input: $input) {
      user {
        id
        firstName
        lastName
        intensityPreferences
        genrePreferences
        latitude
        longitude
        notificationDistance
        distanceUnit
      }
    }
  }
`)

export const ProfileEditScreen = () => {
  const { toast } = useToast()
  const navigate = useNavigate()

  const { viewer, refetch } = useViewer()

  // Function to save FCM token
  const saveFcmToken = async () => {
    try {
      let serviceWorkerRegistration_: ServiceWorkerRegistration | null = null

      const getOrRegisterWorker = async () => {
        console.log("Worker URL:", workerUrl)

        // Check if we're in a secure context
        if (!window.isSecureContext) {
          console.error(
            "Service Worker registration failed: Not in a secure context (HTTPS required)"
          )
          toast({
            title: "Notification Setup Issue",
            description: "Push notifications require HTTPS. Please use a secure connection.",
            variant: "destructive",
          })
          return null
        }

        if (serviceWorkerRegistration_) {
          console.log("Using existing registration")
          return serviceWorkerRegistration_
        }
        try {
          console.log("Registering service worker at:", workerUrl)
          const registration = await navigator.serviceWorker.register(workerUrl, {
            scope: "/",
          })
          console.log("Service worker registered successfully:", registration)
          serviceWorkerRegistration_ = registration
          return registration
        } catch (error) {
          console.error("Service worker registration failed:", error)
          throw error
        }
      }
      const vapidKey =
        "BI-t35xdWhmQ2p7gl8RHty3aOyYddwZdMXOkONq64KhK6zFzQCRzOtPPKzcReTzJveZZo7yg50XrB-MJ_aRYctU" // VAPID key (removed trailing space)

      let app: FirebaseApp | null = null
      let messaging: Messaging | null = null

      app = initializeApp(firebaseConfig)
      messaging = getMessaging(app)

      const serviceWorkerRegistration = await getOrRegisterWorker()

      // Check if we got a valid registration before proceeding
      if (!serviceWorkerRegistration) {
        console.error("Failed to register service worker")
        return false
      }

      invariant(messaging)
      const currentToken = await getToken(messaging, {
        vapidKey,
        serviceWorkerRegistration, // Now this is guaranteed to be a valid ServiceWorkerRegistration
      })
      const cleanup = onMessage(messaging, (payload) => {
        console.log("Message received. ", payload)
        toast({
          title: payload.notification?.title || "New Notification",
          description: payload.notification?.body || "",
        })
      })
      console.log(cleanup)
      if (currentToken) {
        // Save the token via mutation
        await exec({
          variables: {
            input: {
              id: viewer.id,
              userInput: {
                fcm_push_token: currentToken,
              },
            },
          },
        })
        console.log("FCM token saved:", currentToken)
        return true
      } else {
        console.log("No FCM token available")
        return false
      }
    } catch (error) {
      console.error("Error getting FCM token:", error)
      return false
    }
  }

  // Function to request notification permission
  const requestNotificationPermission = async () => {
    if (!("Notification" in window)) {
      toast({
        title: "Notifications Not Supported",
        description: "Your browser doesn't support push notifications.",
        variant: "destructive",
      })
      form.setValue("desktopNotifsEnabled", false)
      return
    }

    try {
      const permission = await Notification.requestPermission()

      if (permission === "granted") {
        toast({
          title: "Notifications Enabled",
          description: "You will now receive push notifications about new games.",
        })
        form.setValue("desktopNotifsEnabled", true)

        // Get and save FCM token
        try {
          const tokenSaved = await saveFcmToken()
          if (!tokenSaved) {
            toast({
              title: "Notification Setup Issue",
              description: "Could not set up push notifications. Please try again later.",
              variant: "destructive",
            })
          }
        } catch (error) {
          console.error("Error getting FCM token:", error)
          toast({
            title: "Notification Setup Error",
            description: "There was a problem setting up push notifications.",
            variant: "destructive",
          })
        }
      } else if (permission === "denied") {
        toast({
          title: "Notifications Blocked",
          description: "Please enable notifications in your browser settings to receive alerts.",
          variant: "destructive",
        })
        form.setValue("desktopNotifsEnabled", false)
      } else {
        // Permission is "default" (prompt closed without a selection)
        toast({
          title: "Notifications Not Enabled",
          description: "You won't receive push notifications about new games.",
        })
        form.setValue("desktopNotifsEnabled", false)
      }
    } catch (error) {
      console.error("Error requesting notification permission:", error)
      toast({
        title: "Notification Error",
        description: "An error occurred while setting up notifications.",
        variant: "destructive",
      })
      form.setValue("desktopNotifsEnabled", false)
    }
  }

  console.log("Viewer Data:", {
    viewer,
    defaultValues: {
      firstName: viewer.firstName,
      lastName: viewer.lastName,
      email: viewer.email,
      intensityPreferences: viewer.intensityPreferences,
      genrePreferences: viewer.genrePreferences,
      emailNotifsEnabled: viewer.emailNotifsEnabled,
      textNotifsEnabled: viewer.textNotifsEnabled,
      desktopNotifsEnabled: viewer.desktopNotifsEnabled,
      emailSummariesEnabled: viewer.emailSummariesEnabled,
      latitude: viewer.latitude,
      longitude: viewer.longitude,
    },
  })

  const formSchema = z.object({
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
    intensityPreferences: z.array(z.enum(["casual", "moderate", "competitive"] as const)),
    genrePreferences: z.array(z.enum(["strategy", "party", "rpg", "word"] as const)),
    emailNotifsEnabled: z.boolean(),
    textNotifsEnabled: z.boolean(),
    desktopNotifsEnabled: z.boolean(),
    emailSummariesEnabled: z.boolean(),
    latitude: z.preprocess((val) => (val === "" ? null : Number(val)), z.number().nullable()),
    longitude: z.preprocess((val) => (val === "" ? null : Number(val)), z.number().nullable()),
    notificationDistance: z.preprocess((val) => (val === "" ? 5 : Number(val)), z.number().min(0)),
    distanceUnit: z.enum(["MILES", "KILOMETERS"] as const),
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: viewer.firstName ?? "",
      lastName: viewer.lastName ?? "",
      email: viewer.email ?? "",
      intensityPreferences: viewer.intensityPreferences ?? [],
      genrePreferences: viewer.genrePreferences ?? [],
      emailNotifsEnabled: viewer.emailNotifsEnabled,
      textNotifsEnabled: viewer.textNotifsEnabled,
      desktopNotifsEnabled: viewer.desktopNotifsEnabled,
      emailSummariesEnabled: viewer.emailSummariesEnabled,
      latitude: viewer.latitude ?? null,
      longitude: viewer.longitude ?? null,
      notificationDistance: viewer.notificationDistance ?? 5,
      distanceUnit: viewer.distanceUnit
        ? (viewer.distanceUnit.toUpperCase() as DistanceUnit)
        : DistanceUnit.Miles,
    },
  })

  console.log("Form Values:", form.getValues())

  const [exec, mutationResult] = useSafeMutation(mutation)
  useValidationErrors(form.setError, mutationResult)

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    // If desktop notifications are enabled but we don't have a token yet, try to get one
    if (values.desktopNotifsEnabled && !viewer.fcmPushToken) {
      await saveFcmToken()
    }

    const result = await exec({
      variables: {
        input: {
          id: viewer.id,
          userInput: {
            firstName: values.firstName,
            lastName: values.lastName,
            intensityPreferences: values.intensityPreferences as GameIntensity[],
            genrePreferences: values.genrePreferences as GameGenre[],
            emailNotifsEnabled: values.emailNotifsEnabled,
            textNotifsEnabled: values.textNotifsEnabled,
            desktopNotifsEnabled: values.desktopNotifsEnabled,
            emailSummariesEnabled: values.emailSummariesEnabled,
            latitude: values.latitude,
            longitude: values.longitude,
            notificationDistance: values.notificationDistance,
            distanceUnit: values.distanceUnit as DistanceUnit,
          },
        },
      },
    })

    if (!result.errors) {
      await refetch()
      toast({
        title: "Profile Updated",
        description: "Your profile has been successfully updated.",
      })
      navigate(rootPath({}))
    }
  }

  return (
    <TablePageLayout>
      <Link to={profilePath({})}>
        &larr; <span className="align-text-bottom text-xs font-bold uppercase">Back</span>
      </Link>
      <div className="flex flex-1 flex-col">
        <div className="bottom-1 mb-4 flex items-center justify-between border-b py-4">
          <h1 className="text-2xl">Edit Details</h1>
          {/* <LinkButton to={}>Update Password</LinkButton> */}
        </div>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="max-w-3xl space-y-8">
              <Section variant="grayBackground" className="space-y-4 p-6">
                <div className="flex items-center gap-2">
                  <UserCircle className="h-5 w-5" />
                  <h3 className="text-lg font-semibold">Details</h3>
                </div>
                <p className="text-sm text-gray-600">
                  Your appearance on Let's Do Games!
                  <br />
                  Your email will not be shared with others.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <TextField control={form.control} name="firstName" label="First Name" required />
                  <TextField control={form.control} name="lastName" label="Last Name" required />
                </div>
                <TextField control={form.control} name="email" label="Email address" disabled />
                <div className="rounded border p-8">
                  <AvatarUpload userId={viewer.id} />
                </div>
              </Section>

              <Section variant="grayBackground" className="space-y-4 p-6">
                <div className="flex items-center gap-2">
                  <Bell className="h-5 w-5" />
                  <h3 className="text-lg font-semibold">Notification Preferences</h3>
                </div>
                <p className="text-sm text-gray-600">
                  When do you want to be alerted about games near you?
                </p>
                <div className="space-y-4">
                  <input type="hidden" {...form.register("latitude")} />
                  <input type="hidden" {...form.register("longitude")} />
                  <div className="flex items-center gap-4">
                    <div className="flex-grow">
                      <PlacesAutocomplete
                        onLocationSelect={(lat, lng) => {
                          form.setValue("latitude", lat)
                          form.setValue("longitude", lng)
                          // Trigger re-render by updating form state
                          form.trigger("latitude")
                          form.trigger("longitude")
                        }}
                        className="w-full"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      {form.getValues("latitude") && form.getValues("longitude") && (
                        <span className="text-sm text-gray-600">
                          Your current location:{" "}
                          {`${form.getValues("latitude")?.toFixed(4) ?? ""}, ${form.getValues("longitude")?.toFixed(4) ?? ""}`}
                        </span>
                      )}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="flex flex-shrink-0 items-center gap-2"
                        onClick={async () => {
                          if ("geolocation" in navigator) {
                            try {
                              const permissionStatus = await navigator.permissions.query({
                                name: "geolocation",
                              })

                              if (permissionStatus.state === "denied") {
                                toast({
                                  title: "Location Access Denied",
                                  description:
                                    "Please enable location access in your browser settings to use this feature.",
                                  variant: "destructive",
                                })
                                return
                              }

                              navigator.geolocation.getCurrentPosition(
                                (position) => {
                                  form.setValue("latitude", position.coords.latitude)
                                  form.setValue("longitude", position.coords.longitude)
                                  // Trigger re-render by updating form state
                                  form.trigger("latitude")
                                  form.trigger("longitude")
                                  toast({
                                    title: "Location Updated",
                                    description: "Your current location has been filled in.",
                                  })
                                },
                                (error) => {
                                  toast({
                                    title: "Location Error",
                                    description:
                                      "Could not get your current location. Please check your browser permissions.",
                                    variant: "destructive",
                                  })
                                  console.error("Geolocation error:", error)
                                }
                              )
                            } catch (error) {
                              toast({
                                title: "Location Error",
                                description: "An error occurred while accessing location services.",
                                variant: "destructive",
                              })
                              console.error("Permission check error:", error)
                            }
                          } else {
                            toast({
                              title: "Location Not Supported",
                              description: "Your browser doesn't support geolocation.",
                              variant: "destructive",
                            })
                          }
                        }}
                      >
                        <MapPinHouseIcon className="h-4 w-4" />
                        Use Current Location
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-end gap-4">
                    <TextField
                      control={form.control}
                      name="notificationDistance"
                      label="Distance"
                      type="number"
                      step="any"
                    />
                    <Select
                      defaultValue={form.getValues("distanceUnit")}
                      onValueChange={(value: "KILOMETERS" | "MILES") =>
                        form.setValue("distanceUnit", value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select unit" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="MILES">Miles</SelectItem>
                        <SelectItem value="KILOMETERS">Kilometers</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Notify me by:</label>
                    <div className="flex gap-6">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="email-notifs"
                          checked={form.watch("emailNotifsEnabled")}
                          onCheckedChange={(checked) =>
                            form.setValue("emailNotifsEnabled", checked as boolean)
                          }
                        />
                        <label htmlFor="email-notifs" className="text-sm">
                          Email
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="text-notifs"
                          checked={form.watch("textNotifsEnabled")}
                          onCheckedChange={(checked) =>
                            form.setValue("textNotifsEnabled", checked as boolean)
                          }
                        />
                        <label htmlFor="text-notifs" className="text-sm">
                          Text
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="desktop-notifs"
                          checked={form.watch("desktopNotifsEnabled")}
                          onCheckedChange={(checked) => {
                            form.setValue("desktopNotifsEnabled", checked as boolean)
                            if (checked) {
                              requestNotificationPermission()
                            }
                          }}
                        />
                        <label htmlFor="desktop-notifs" className="text-sm">
                          Push Notification
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="email-summaries"
                          checked={form.watch("emailSummariesEnabled")}
                          onCheckedChange={(checked) =>
                            form.setValue("emailSummariesEnabled", checked as boolean)
                          }
                        />
                        <label htmlFor="email-summaries" className="text-sm">
                          Email Summaries
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </Section>

              <Section variant="grayBackground" className="space-y-4 p-6">
                <div className="flex items-center gap-2">
                  <Dices className="h-5 w-5" />
                  <h3 className="text-lg font-semibold">Game Preferences</h3>
                </div>
                <p className="text-sm text-gray-600">
                  What types of games do you want to be notified about?
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="mb-2 block text-sm font-medium">
                      Preferred Game Intensities
                    </label>
                    <div className="space-y-2">
                      {Object.values(GAME_INTENSITIES).map((intensity) => {
                        return (
                          <div key={intensity.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`intensity-${intensity.id}`}
                              value={intensity.id}
                              checked={form.watch("intensityPreferences").includes(intensity.id)}
                              onCheckedChange={(checked) => {
                                const current = form.getValues("intensityPreferences")
                                if (checked) {
                                  form.setValue("intensityPreferences", [...current, intensity.id])
                                } else {
                                  form.setValue(
                                    "intensityPreferences",
                                    current.filter((id) => id !== intensity.id)
                                  )
                                }
                              }}
                            />
                            <label htmlFor={`intensity-${intensity.id}`} className="text-sm">
                              {intensity.label}
                              <span className="ml-2 text-xs text-gray-500">
                                {intensity.description}
                              </span>
                            </label>
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium">Preferred Game Genres</label>
                    <div className="space-y-2">
                      {Object.values(GAME_GENRES).map((genre) => (
                        <div key={genre.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`genre-${genre.id}`}
                            value={genre.id}
                            checked={form.watch("genrePreferences").includes(genre.id)}
                            onCheckedChange={(checked) => {
                              const current = form.getValues("genrePreferences")
                              if (checked) {
                                form.setValue("genrePreferences", [...current, genre.id])
                              } else {
                                form.setValue(
                                  "genrePreferences",
                                  current.filter((id) => id !== genre.id)
                                )
                              }
                            }}
                          />
                          <label htmlFor={`genre-${genre.id}`} className="text-sm">
                            {genre.label}
                            <span className="ml-2 text-xs text-gray-500">{genre.description}</span>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </Section>
            </div>
            <div className="mt-4 flex items-center gap-4 border-t pt-10">
              <Button type="submit" disabled={form.formState.isSubmitting}>
                Save &amp; Update
              </Button>
              <Link to={profilePath({})} className="text-sm text-neutral-400">
                Close &amp; Cancel
              </Link>
              {/* <div className="flex flex-1 justify-end">
                <Link to={profilePath({})} className="text-sm text-neutral-400">
                  Request account removal
                </Link>
              </div> */}
            </div>
          </form>
        </Form>
      </div>
    </TablePageLayout>
  )
}
